import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import * as yup from "yup";

import { useForm } from "@hooks/useForm";
import { useEfemeralUrlError } from "@hooks/useEfemeralUrlError";
import { signUp } from "@services/index";
import { validateToken } from "@services/validateTeamToken";

import { Input, Form } from "@components/common/Input";
import PhoneInput from "@components/common/Input/PhoneInput";
import Button from "@components/common/Button";
import ReactSelect from "@components/common/ReactSelect";
import { Divider } from "@components/common/Divider";

const initialState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  whyUseFixr: "",
  password: "",
  teamToken: "",
};

const SignUpSchema = yup.object({
  name: yup.string().required(),
  email: yup
    .string()
    .email("your email address seems to be invalid")
    .required(),
  phone: yup.string().required(),
  company: yup.string().required(),
  whyUseFixr: yup.string().required(),
  password: yup.string().required().min(8),
  teamToken: yup.string(),
  timeZone: yup.string(),
});

const getQueryParam = (param: string | string[] = ""): string => {
  return Array.isArray(param) ? param[0] : param;
};

const optionsMoreInterested = [
  {
    label: "Fixing issues in my instance",
    value: "Fixing issues in my instance",
  },
  {
    label: "Tracking instance overall health",
    value: "Tracking instance overall health",
  },
  {
    label: "Guarantee that the development team follow the best practices",
    value: "Guarantee that the development team follow the best practices",
  },
  {
    label: "Educate the development team",
    value: "Educate the development team",
  },
  {
    label: "Create an action plan to improve instance’s quality",
    value: "Create an action plan to improve instance’s quality",
  },
];

export const SignUpForm = ({
  accentureLoginUrl = "/login/sso",
}: {
  accentureLoginUrl?: string;
}) => {
  const router = useRouter();

  const [showPassword, toggleShowPassword] = useState(false);
  const [timeZone, setTimeZone] = useState("");
  const [invitedUserEmail, setInvitedUserEmail] = useState("");
  const [invitedUserTeamId, setInvitedUserTeamId] = useState("");
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const urlError = useEfemeralUrlError();

  const {
    token: teamToken,
    source: sourceParam,
    plan: planParam,
  } = router.query;

  const token = getQueryParam(teamToken);
  const source = getQueryParam(sourceParam);
  const plan = getQueryParam(planParam);

  useEffect(() => {
    if (token && !isValidatingToken) {
      (async () => {
        try {
          setIsValidatingToken(true);
          const { email, team } = await validateToken(token);
          if (email && team) {
            setInvitedUserEmail(email);
            setInvitedUserTeamId(team);
          }
        } finally {
          setIsValidatingToken(false);
        }
      })();
    }
  }, [token]);

  useEffect(
    () => setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone),
    [],
  );
  const { useField, status, handleSubmit, formErrors } = useForm(
    initialState,
    SignUpSchema,
    (form) =>
      signUp({
        ...form,
        teamToken: token || "",
        teamId: invitedUserTeamId,
        source,
        plan,
        timeZone,
      }),
  );

  const name = useField("name");
  const email = useField("email");
  const phone = useField("phone");
  const company = useField("company");
  const password = useField("password");
  const whyUseFixr = useField("whyUseFixr");

  useEffect(() => {
    localStorage.setItem("email", email.value);

    if (status === "DONE") router.push("/account/verify");
  }, [status]);

  useEffect(() => {
    if (invitedUserEmail) email.onChange(invitedUserEmail);
  }, [invitedUserEmail]);

  return (
    <section>
      <a style={{ textDecoration: "none" }} href={accentureLoginUrl}>
        <Button outline block color="white" icon={{ name: "accenture" }}>
          Log in with Accenture account
        </Button>
      </a>

      <Divider text="OR" />

      <Form
        onSubmit={handleSubmit}
        errors={urlError ? formErrors.concat(urlError) : formErrors}
      >
        <Input {...name} label="Full Name" />
        <Input
          {...email}
          disabled={!!invitedUserEmail}
          readOnly={!!invitedUserEmail}
          type="email"
          label="Work Email"
        />
        <Input {...company} label="Company" />
        <PhoneInput {...phone} label="Phone Number" />
        <ReactSelect
          smallDropdown
          options={optionsMoreInterested}
          label="What are you more interested in?"
          placeholder="Choose an option"
          {...whyUseFixr}
        />
        <Input
          {...password}
          type={showPassword ? "text" : "password"}
          icon={showPassword ? "eye" : "eye-close"}
          iconAction={() => toggleShowPassword(!showPassword)}
          iconRight
          placeholder="Min 8 characters"
          label="Password"
        />
        <Form.Actions>
          <Button
            color="blue"
            disabled={status === "LOADING" || status !== "VALID"}
            loading={status === "LOADING"}
            type="submit"
            block
          >
            Create Account
          </Button>
        </Form.Actions>
      </Form>
    </section>
  );
};
